import React, { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Checkbox, DatePicker, message, Button } from 'antd';
import { PaperPlaneTilt } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import contractService from '../../../../services/oneButtonServices/contractService';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';

/** Subset of `GenerateContractContant` for Service Finance primary applicant (Trinity modal). */
const RESIDENCE = [
    { value: 'Own', label: 'Own' },
    { value: 'Rent', label: 'Rent' },
];
const PROPERTY_TYPES = [
    { value: 'Single Family', label: 'Single Family' },
    { value: 'Manufactured Home', label: 'Manufactured Home' },
];
const DOCUMENT_TYPES = [
    { value: 'Passport', label: 'Passport' },
    { value: 'Driver License', label: 'Driver License' },
    { value: 'State ID', label: 'State ID' },
    { value: 'Military ID', label: 'Military ID' },
    { value: 'Visa', label: 'Visa' },
    { value: 'Green Card', label: 'Green Card' },
];
const CITIZEN = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
];

export type ServiceFinanceSubmitModalRow = {
    quoteIdRaw: string;
    totalEpcDisplay: string;
};

type FormValues = {
    programType: string;
    dob: unknown;
    ssn: string;
    employerName: string;
    grossMonthIncome: number;
    residence: string;
    mortgageRentalAmount: number;
    propertyType: string;
    iD_Type: string;
    documentNumber: string;
    state: string;
    issueDate: unknown;
    expirationDate: unknown;
    issuer: string;
    uS_CitizenShip: string;
    phoneType: string;
    phoneNumber: string;
};

const initialValues: Partial<FormValues> = {
    grossMonthIncome: 0,
    mortgageRentalAmount: 0,
    uS_CitizenShip: 'true',
};

/**
 * Trinity `ServiceFinanceApplicationSubmissionModalComponent` — primary applicant only,
 * POST `SubmitApplication` with one object in the array (parity with Angular `submit()` single-applicant path).
 */
export function ServiceFinanceSubmitModal({
    open,
    onClose,
    row,
    opportunityId,
    salesPersonEmail,
    onCompleted,
    theme: themeProp = 'light',
}: {
    open: boolean;
    onClose: () => void;
    row: ServiceFinanceSubmitModalRow | null;
    opportunityId: string | number | null | undefined;
    salesPersonEmail: string;
    onCompleted: (appId?: string) => void;
    /** Same as Agreements / `DashboardModal` — avoids default-dark shell in light UI. */
    theme?: 'dark' | 'light';
}) {
    const modalTheme: 'dark' | 'light' = themeProp === 'dark' ? 'dark' : 'light';
    const [form] = Form.useForm<FormValues>();
    const [loading, setLoading] = useState(false);
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        if (open) {
            form.resetFields();
            form.setFieldsValue(initialValues);
            setAccepted(false);
        }
    }, [open, form]);

    const handleSubmit = async () => {
        if (!accepted) {
            message.warning('Please acknowledge the disclosure before submitting.');
            return;
        }
        if (!row?.quoteIdRaw || opportunityId == null || String(opportunityId).trim() === '') {
            message.error('Missing opportunity or quote on this row.');
            return;
        }
        try {
            const v = await form.validateFields();
            setLoading(true);
            const salesAmount =
                parseFloat(String(row.totalEpcDisplay ?? '').replace(/[^0-9.-]/g, '')) || 0;
            const saveData = [
                {
                    ...v,
                    dob: v.dob ? dayjs(v.dob as any).format('MM/DD/YYYY') : '',
                    issueDate: v.issueDate ? dayjs(v.issueDate as any).format('MM/DD/YYYY') : '',
                    expirationDate: v.expirationDate ? dayjs(v.expirationDate as any).format('MM/DD/YYYY') : '',
                    opportunityId: Number(opportunityId),
                    salePersonEmail: salesPersonEmail || '',
                    salesAmount,
                    quoteId: row.quoteIdRaw,
                    mortgageRentalAmount: v.mortgageRentalAmount ?? 0,
                    grossMonthIncome: v.grossMonthIncome ?? 0,
                    uS_CitizenShip: v.uS_CitizenShip === 'true',
                    programType: v.programType,
                    isPrimary: true,
                    isCoApplicant: false,
                    customerId: 0,
                },
            ];
            const res: any = await contractService.submitServiceFinanceApplication(saveData);
            setLoading(false);
            if (res && res.IsSuccessful && res.Data) {
                message.success('Service Finance application submitted.');
                const appId = res.Data?.AppId != null ? String(res.Data.AppId) : undefined;
                onCompleted(appId);
                onClose();
            } else {
                message.error(res?.Message ? String(res.Message) : 'Submit was not successful.');
            }
        } catch (e: unknown) {
            setLoading(false);
            const err = e as { errorFields?: unknown; message?: string };
            if (err?.errorFields) return;
            message.error(err?.message || 'Submit failed.');
        }
    };

    return (
        <DashboardModal
            open={open && !!row}
            title="Submit application to Service Finance"
            onClose={onClose}
            theme={modalTheme}
            footer={
                <Button type="primary" onClick={() => void handleSubmit()} loading={loading} icon={<PaperPlaneTilt size={14} weight="bold" />}>
                    Submit
                </Button>
            }
            width={800}
            destroyOnClose
            maskClosable={false}
        >
            <p style={{ marginBottom: 12, opacity: 0.85 }}>
                Same fields as Trinity Service Finance — program type must match your finance partner setup (often derived from
                term/APR in oneBUTTON).
            </p>
            <Form form={form} layout="vertical" size="small" initialValues={initialValues}>
                <Form.Item
                    label="Program type"
                    name="programType"
                    rules={[{ required: true, message: 'Program type is required' }]}
                >
                    <Input placeholder="Program type code" autoComplete="off" />
                </Form.Item>
                <Form.Item label="Date of birth" name="dob" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} format="MM/DD/YYYY" />
                </Form.Item>
                <Form.Item
                    label="SSN"
                    name="ssn"
                    rules={[{ required: true }, { pattern: /^\d{3}-\d{2}-\d{4}$/, message: 'Use ###-##-####' }]}
                >
                    <Input placeholder="000-00-0000" autoComplete="off" />
                </Form.Item>
                <Form.Item label="Employer name" name="employerName" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item label="Gross monthly income" name="grossMonthIncome" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
                <Form.Item label="Residence" name="residence" rules={[{ required: true }]}>
                    <OBDSelect options={RESIDENCE} placeholder="Select" />
                </Form.Item>
                <Form.Item label="Mortgage / rent amount" name="mortgageRentalAmount" rules={[{ required: true }]}>
                    <InputNumber style={{ width: '100%' }} min={0} />
                </Form.Item>
                <Form.Item label="Property type" name="propertyType" rules={[{ required: true }]}>
                    <OBDSelect options={PROPERTY_TYPES} placeholder="Select" />
                </Form.Item>
                <Form.Item label="ID type" name="iD_Type" rules={[{ required: true }]}>
                    <OBDSelect options={DOCUMENT_TYPES} placeholder="Select" />
                </Form.Item>
                <Form.Item label="Document number" name="documentNumber" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item label="State (ID)" name="state" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item label="Issue date" name="issueDate" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} format="MM/DD/YYYY" />
                </Form.Item>
                <Form.Item label="Expiration date" name="expirationDate" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} format="MM/DD/YYYY" />
                </Form.Item>
                <Form.Item label="Issuer" name="issuer" rules={[{ required: true }]}>
                    <Input />
                </Form.Item>
                <Form.Item label="U.S. citizenship" name="uS_CitizenShip" rules={[{ required: true }]}>
                    <OBDSelect options={CITIZEN} />
                </Form.Item>
                <Form.Item label="Phone type" name="phoneType" rules={[{ required: true }]}>
                    <Input placeholder="e.g. Mobile" />
                </Form.Item>
                <Form.Item label="Phone number" name="phoneNumber" rules={[{ required: true, message: 'Phone is required' }]}>
                    <Input placeholder="(000) 000-0000" />
                </Form.Item>
            </Form>
            <Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} style={{ marginTop: 8 }}>
                I acknowledge the Service Finance disclosure statements (same as Trinity submit application).
            </Checkbox>
        </DashboardModal>
    );
}

export default ServiceFinanceSubmitModal;
